import { useEffect, useState } from 'react';
import { Logo } from '../components/Logo';
import { Loading } from '../components/Loading';
import { SlotPicker } from '../components/SlotPicker';
import {
  getBookingByToken,
  postCancelBooking,
  postRescheduleBooking,
} from '../lib/api/client';
import type {
  AvailabilitySlot,
  BookingByTokenSuccess,
  QualifySuccess,
} from '../lib/api/types';

/**
 * /r/{token} — prospect-facing reschedule + cancel page.
 *
 * Auth-free; the reschedule_token is the bearer. V2 task #28 will
 * upgrade this to a real auth flow if we ever decide tokens are
 * insufficient (we won't — they're plenty for V1 since they're
 * UUIDs delivered out-of-band in calendar invites).
 *
 * State machine:
 *   loading -> viewing (active confirmed booking)
 *           |  past (already happened)
 *           |  cancelled (already cancelled)
 *           |  not-found (bad / expired token)
 *           |  error (network / server)
 *
 *   viewing -> confirming-cancel -> cancelling -> cancelled-success
 *           -> picking-slot -> rescheduling -> rescheduled-success
 */

type Booking = BookingByTokenSuccess['booking'];

type Phase =
  | { state: 'loading' }
  | { state: 'not_found' }
  | { state: 'error'; message: string }
  | { state: 'past'; booking: Booking }
  | { state: 'cancelled_already'; booking: Booking }
  | { state: 'viewing'; booking: Booking }
  | { state: 'confirming_cancel'; booking: Booking }
  | { state: 'cancelling'; booking: Booking }
  | { state: 'cancelled_success'; booking: Booking }
  | { state: 'picking_slot'; booking: Booking }
  | { state: 'rescheduling'; booking: Booking; slot: AvailabilitySlot }
  | {
      state: 'rescheduled_success';
      previousBooking: Booking;
      newMeetingStartUtc: string;
      newRescheduleToken: string;
      zoomJoinUrl: string | null;
    };

const TZ = 'America/New_York';

function formatMeetingTime(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(d);
}

function getProspectTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * Build the QualifySuccess shape SlotPicker needs from a Booking.
 *
 * Reschedules are advisor-first by definition — we already know which
 * advisor the prospect met with, so the slot picker reuses the same
 * "Pick a time with {firstName}" advisor-first UI rather than slot-first.
 * ruleHit is set to OWNERSHIP_ACCOUNT for type-narrowing only; the
 * server doesn't run the routing engine on a reschedule.
 */
function syntheticMatch(booking: Booking): QualifySuccess {
  return {
    ok: true,
    kind: 'advisor_first',
    sessionId: 'reschedule',
    advisor: {
      id: booking.advisor.id,
      firstName: booking.advisor.firstName,
      lastName: booking.advisor.lastName,
    },
    ruleHit: 'OWNERSHIP_ACCOUNT',
    poolSlug: null,
    // 60-min floor for reschedules (vs the 24h floor for new public
    // bookings) — when a prospect confirms a reschedule on a call, we
    // want next-day slots visible. Aligns with /api/availability's
    // staff-session bypass (PR pairing).
    minLeadTimeMinutes: 60,
    effectiveLeadTimeMinutes: 60,
    meetingDurationMinutes: booking.meetingDurationMinutes,
  };
}

export function RescheduleBooking() {
  const [phase, setPhase] = useState<Phase>({ state: 'loading' });

  // Read token from /r/<token> path.
  const token = (() => {
    const path = window.location.pathname;
    const match = path.match(/^\/r\/([0-9a-f-]+)/i);
    return match ? match[1] : null;
  })();

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setPhase({ state: 'not_found' });
      return;
    }
    (async () => {
      try {
        const res = await getBookingByToken(token);
        if (cancelled) return;
        if (!res.ok) {
          if (res.error === 'not_found') {
            setPhase({ state: 'not_found' });
          } else {
            setPhase({ state: 'error', message: res.message });
          }
          return;
        }
        const b = res.booking;
        if (b.status === 'cancelled') {
          setPhase({ state: 'cancelled_already', booking: b });
        } else if (b.inPast || b.status === 'completed' || b.status === 'no_show') {
          setPhase({ state: 'past', booking: b });
        } else {
          setPhase({ state: 'viewing', booking: b });
        }
      } catch (e) {
        if (cancelled) return;
        setPhase({
          state: 'error',
          message: e instanceof Error ? e.message : 'Network error',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleConfirmCancel() {
    if (phase.state !== 'confirming_cancel' || !token) return;
    const booking = phase.booking;
    setPhase({ state: 'cancelling', booking });
    try {
      const res = await postCancelBooking({ token });
      if (res.ok) {
        setPhase({ state: 'cancelled_success', booking });
      } else {
        setPhase({ state: 'error', message: res.message });
      }
    } catch (e) {
      setPhase({
        state: 'error',
        message: e instanceof Error ? e.message : 'Network error',
      });
    }
  }

  async function handleSelectSlot(slot: AvailabilitySlot, tz: string) {
    if (phase.state !== 'picking_slot' || !token) return;
    const booking = phase.booking;
    setPhase({ state: 'rescheduling', booking, slot });
    try {
      const res = await postRescheduleBooking({
        token,
        slotStartUtc: slot.startUtc,
        prospectTimezone: tz ?? getProspectTimezone(),
      });
      if (res.ok) {
        setPhase({
          state: 'rescheduled_success',
          previousBooking: booking,
          newMeetingStartUtc: res.newMeetingStartUtc,
          newRescheduleToken: res.newRescheduleToken,
          zoomJoinUrl: res.zoomJoinUrl,
        });
      } else if (res.error === 'slot_unavailable') {
        // Race lost — bounce back to picker.
        setPhase({ state: 'picking_slot', booking });
        // (Could add a toast — V1 just resets the picker.)
      } else {
        setPhase({ state: 'error', message: res.message });
      }
    } catch (e) {
      setPhase({
        state: 'error',
        message: e instanceof Error ? e.message : 'Network error',
      });
    }
  }

  return (
    <div className="app">
      <header className="header">
        <div className="logo-wrap">
          <Logo className="logo" />
          <p className="tagline">A better annuity buying experience</p>
        </div>
        <div className="accent-bar">
          <div className="accent-bar-blue" />
          <div className="accent-bar-red" />
        </div>
      </header>

      <main className="main">
        {phase.state === 'loading' && <Loading message="Looking up your meeting…" />}

        {phase.state === 'not_found' && (
          <div className="reschedule-state">
            <h2>Link not found</h2>
            <p>
              We couldn't find a meeting matching this link. It may have
              expired or already been rescheduled. If you believe this is
              an error, reach out to{' '}
              <a href="mailto:scheduler@annuityassociation.com">
                scheduler@annuityassociation.com
              </a>
              .
            </p>
          </div>
        )}

        {phase.state === 'error' && (
          <div className="reschedule-state reschedule-state-error">
            <h2>Something went wrong</h2>
            <p>{phase.message}</p>
            <p>
              Please try again, or contact{' '}
              <a href="mailto:scheduler@annuityassociation.com">
                scheduler@annuityassociation.com
              </a>{' '}
              for help.
            </p>
          </div>
        )}

        {phase.state === 'past' && (
          <div className="reschedule-state">
            <h2>This meeting has already happened</h2>
            <p>
              Your meeting with {phase.booking.advisor.firstName}{' '}
              {phase.booking.advisor.lastName} on{' '}
              <strong>{formatMeetingTime(phase.booking.meetingStartUtc)}</strong>{' '}
              has already taken place. If you need to schedule a follow-up,
              reach out to{' '}
              <a href="mailto:scheduler@annuityassociation.com">
                scheduler@annuityassociation.com
              </a>
              .
            </p>
          </div>
        )}

        {phase.state === 'cancelled_already' && (
          <div className="reschedule-state">
            <h2>This meeting has been cancelled</h2>
            <p>
              Your meeting with {phase.booking.advisor.firstName} was
              cancelled and is no longer on the calendar. To book a new
              one, visit{' '}
              <a href="/">scheduler.annuityassociation.com</a>.
            </p>
          </div>
        )}

        {phase.state === 'viewing' && (
          <div className="reschedule-state">
            <h2>Hi {phase.booking.prospectFirstName},</h2>
            <p className="reschedule-meeting-summary">
              You have a 1st Appointment with{' '}
              <strong>
                {phase.booking.advisor.firstName} {phase.booking.advisor.lastName}
              </strong>{' '}
              on{' '}
              <strong>{formatMeetingTime(phase.booking.meetingStartUtc)}</strong>.
            </p>
            <p>What would you like to do?</p>
            <div className="reschedule-actions">
              <button
                type="button"
                className="submit-btn"
                onClick={() =>
                  setPhase({ state: 'picking_slot', booking: phase.booking })
                }
              >
                Pick a new time
              </button>
              <button
                type="button"
                className="link-btn"
                onClick={() =>
                  setPhase({ state: 'confirming_cancel', booking: phase.booking })
                }
              >
                Cancel meeting
              </button>
            </div>
          </div>
        )}

        {phase.state === 'confirming_cancel' && (
          <div className="reschedule-state">
            <h2>Cancel your meeting?</h2>
            <p>
              You're about to cancel your 1st Appointment with{' '}
              <strong>
                {phase.booking.advisor.firstName} {phase.booking.advisor.lastName}
              </strong>{' '}
              on{' '}
              <strong>{formatMeetingTime(phase.booking.meetingStartUtc)}</strong>.
            </p>
            <p>This will remove the event from your calendar and release the time slot.</p>
            <div className="reschedule-actions">
              <button
                type="button"
                className="submit-btn submit-btn-danger"
                onClick={handleConfirmCancel}
              >
                Yes, cancel my meeting
              </button>
              <button
                type="button"
                className="link-btn"
                onClick={() =>
                  setPhase({ state: 'viewing', booking: phase.booking })
                }
              >
                Never mind, keep it
              </button>
            </div>
          </div>
        )}

        {phase.state === 'cancelling' && <Loading message="Cancelling your meeting…" />}

        {phase.state === 'cancelled_success' && (
          <div className="reschedule-state">
            <div className="booked-badge">
              <span className="booked-check">✓</span>
              <span>Cancelled</span>
            </div>
            <h2>Your meeting has been cancelled</h2>
            <p>
              We've cancelled your meeting with{' '}
              <strong>{phase.booking.advisor.firstName}</strong> and released the
              time slot. The calendar invite has been removed.
            </p>
            <p>
              If you change your mind, you can{' '}
              <a href="/">book a new meeting</a> any time.
            </p>
          </div>
        )}

        {phase.state === 'picking_slot' && (
          <div className="form-card">
            <SlotPicker
              match={syntheticMatch(phase.booking)}
              windowDays={phase.booking.meetingTypeSlug === 'annuity-anniversary' ? 395 : phase.booking.meetingTypeSlug && phase.booking.meetingTypeSlug !== '1st-meeting' ? 90 : 30}
              meetingTypeSlug={phase.booking.meetingTypeSlug ?? undefined}
              durationMinutes={phase.booking.meetingDurationMinutes}
              onSelectSlot={handleSelectSlot}
              onBack={() =>
                setPhase({ state: 'viewing', booking: phase.booking })
              }
            />
          </div>
        )}

        {phase.state === 'rescheduling' && (
          <Loading message="Moving your meeting…" />
        )}

        {phase.state === 'rescheduled_success' && (
          <div className="reschedule-state">
            <div className="booked-badge">
              <span className="booked-check">✓</span>
              <span>Rescheduled</span>
            </div>
            <h2>
              You're set with{' '}
              {phase.previousBooking.advisor.firstName}
            </h2>
            <div className="booked-meeting">
              <div className="booked-when">
                {formatMeetingTime(phase.newMeetingStartUtc)}
              </div>
              <div className="booked-format">{phase.previousBooking.meetingDurationMinutes}-minute Zoom call</div>
            </div>
            {phase.zoomJoinUrl && (
              <a
                href={phase.zoomJoinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="booked-zoom-btn"
              >
                Join Zoom Meeting
              </a>
            )}
            <p>
              A new calendar invite is on its way to{' '}
              <strong>{phase.previousBooking.prospectEmail}</strong>. The
              previous meeting time has been released.
            </p>
          </div>
        )}
      </main>

      <footer className="footer">
        <p>
          <strong>Annuity Association</strong> · 759 SW Federal Hwy, Stuart, FL
        </p>
        <p className="footer-small">
          © {new Date().getFullYear()} Annuity Association. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
